/**
 * @fileoverview WebSocket client for the FanMessages (Mensajes a la afición) Admin Operator Panel.
 * Connects to the real-time communication channel to receive projection state changes,
 * snapshots, telemetry, and emit operator commands.
 * 
 * @module utilities/WS/Admin/FanMessagesWS
 */

let fanMessagesAdminWebSocket = null;
let isFanMessagesAdminConnected = false;
let reconnectionTimeoutIdentifier = null;
let connectionAttemptCounter = 0;
const MAXIMUM_RECONNECTION_ATTEMPTS = 15;
const RECONNECTION_DELAY_MILLISECONDS = 3000;

/**
 * Establishes a persistent WebSocket connection for the FanMessages admin panel.
 * 
 * @function ConnectWebSocketForFanMessagesAdmin
 * @param {Function} [onMessageReceived=()=>{}] - Callback invoked whenever a message is received.
 * @param {Function} [onStatusChange=()=>{}] - Callback invoked when the connection status changes ('connecting', 'connected', 'reconnecting', 'error', 'disconnected').
 */
export const ConnectWebSocketForFanMessagesAdmin = (
    onMessageReceived = () => {},
    onStatusChange = () => {}
) => {
    if (fanMessagesAdminWebSocket || isFanMessagesAdminConnected) {
        return;
    }

    const establishConnection = () => {
        if (connectionAttemptCounter >= MAXIMUM_RECONNECTION_ATTEMPTS) {
            onStatusChange('error');
            return;
        }

        connectionAttemptCounter += 1;
        onStatusChange('connecting');

        try {
            const webSocketEndpointUrl = `${process.env.REACT_APP_API_URL_WSS}?tipo=superadmin`;
            fanMessagesAdminWebSocket = new WebSocket(webSocketEndpointUrl);

            fanMessagesAdminWebSocket.onopen = () => {
                isFanMessagesAdminConnected = true;
                connectionAttemptCounter = 0;
                onStatusChange('connected');
            };

            fanMessagesAdminWebSocket.onclose = () => {
                isFanMessagesAdminConnected = false;
                fanMessagesAdminWebSocket = null;
                onStatusChange('reconnecting');

                clearTimeout(reconnectionTimeoutIdentifier);
                reconnectionTimeoutIdentifier = setTimeout(establishConnection, RECONNECTION_DELAY_MILLISECONDS);
            };

            fanMessagesAdminWebSocket.onerror = () => {
                onStatusChange('error');
            };

            fanMessagesAdminWebSocket.onmessage = (messageEvent) => {
                try {
                    const parsedPayload = JSON.parse(messageEvent.data);
                    onMessageReceived(parsedPayload);
                } catch (parseError) {
                    // Ignore non-JSON or unformatted packets
                }
            };
        } catch (connectionError) {
            onStatusChange('error');
            clearTimeout(reconnectionTimeoutIdentifier);
            reconnectionTimeoutIdentifier = setTimeout(establishConnection, RECONNECTION_DELAY_MILLISECONDS);
        }
    };

    establishConnection();
};

/**
 * Sends a JSON payload through the active FanMessages admin WebSocket connection.
 * 
 * @function SendWebSocketMessage
 * @param {Object} payloadObject - JSON object to transmit.
 * @returns {boolean} True if successfully sent, false otherwise.
 */
export const SendWebSocketMessage = (payloadObject) => {
    try {
        if (fanMessagesAdminWebSocket && fanMessagesAdminWebSocket.readyState === WebSocket.OPEN) {
            fanMessagesAdminWebSocket.send(JSON.stringify(payloadObject));
            return true;
        }
        return false;
    } catch (sendError) {
        return false;
    }
};

/**
 * Closes and cleans up the FanMessages admin WebSocket connection.
 * 
 * @function DisconnectWebSocketForFanMessagesAdmin
 */
export const DisconnectWebSocketForFanMessagesAdmin = () => {
    try {
        if (reconnectionTimeoutIdentifier) {
            clearTimeout(reconnectionTimeoutIdentifier);
            reconnectionTimeoutIdentifier = null;
        }

        if (fanMessagesAdminWebSocket) {
            fanMessagesAdminWebSocket.onopen = null;
            fanMessagesAdminWebSocket.onclose = null;
            fanMessagesAdminWebSocket.onerror = null;
            fanMessagesAdminWebSocket.onmessage = null;

            if (fanMessagesAdminWebSocket.readyState === WebSocket.OPEN) {
                fanMessagesAdminWebSocket.close();
            } else if (fanMessagesAdminWebSocket.readyState === WebSocket.CONNECTING) {
                const socketToClose = fanMessagesAdminWebSocket;
                socketToClose.onopen = () => {
                    try {
                        socketToClose.close();
                    } catch {
                        // Safe ignore
                    }
                };
            }

            fanMessagesAdminWebSocket = null;
            isFanMessagesAdminConnected = false;
        }
    } catch (teardownError) {
        // Safe teardown
    }
};
